<template>
  <div
    data-test-id="item-modifier-set"
  >
    <q-expansion-item
      v-if="isExpandable"
      expand-icon-toggle
      hide-expand-icon
      :model-value="expanded"
    >
      <template #header>
        <q-item-section>
          <q-item-label class="text-bold text-subtitle1">
            {{ itemModifierSet.modifierSet.name }}
          </q-item-label>
          <q-item-label
            caption
            lines="2"
          >
            <span
              :class="{
                'text-positive': isValid && itemModifierSet.minSelectionCount,
                'text-negative': !isValid && itemModifierSet.minSelectionCount,
                'text-warning': !itemModifierSet.minSelectionCount
              }"
            >
              <q-icon
                v-if="!itemModifierSet.minSelectionCount"
                name="fa-regular fa-circle-question"
              />
              <q-icon
                v-else
                :name="isValid ? 'fa-regular fa-circle-check' : 'fa-regular fa-triangle-exclamation'"
              />
              {{ itemModifierSet.minSelectionCount ? $t('common.required') : $t('common.optional') }} </span> • {{ isValid ? selectedModifierNames : itemModifierSet.choiceCountDescription }}
          </q-item-label>
        </q-item-section>

        <q-item-section
          side
          top
        >
          <q-btn
            v-if="!expanded || (expanded && isValid)"
            :label="expanded ? 'Done' : 'Change'"
            dense
            class="text-underline"
            no-caps
            flat
            @click="expanded = !expanded"
          />
        </q-item-section>
      </template>
      <template #default>
        <div
          class="row"
          :class="$q.screen.gt.xs ? 'q-col-gutter-lg' : 'q-col-gutter-sm'"
        >
          <div
            v-for="modifier in modifiersComputed"
            :key="modifier.id"
            :class="$q.screen.gt.xs ? 'col-6' : 'col-12'"
          >
            <q-card
              flat
              bordered
              style="border-radius: 10px;"
              class="position-relative q-pa-sm"
              :class="{
                'bg-responsive-grey-2': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id),
                'text-red': errorMessage
              }"
              @click="handleClick(modifier)"
            >
              <q-card-section
                horizontal
                class="justify-between items-center q-pa-sm"
                :style="!editMode && reduceOpacity({ item, modifier }) ? 'opacity: 0.3;' : ''"
              >
                <q-icon
                  :name="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'fas fa-square-check' : 'fal fa-square'"
                  :color="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'primary' : errorMessage ? 'red' : 'responsive'"
                  :class="{ 'bg-white': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) }"
                  style="height: 20px;"
                  class="col-auto q-mr-sm"
                  size="sm"
                />
                <q-img
                  v-if="modifier.image"
                  :ratio="1"
                  class="rounded-borders q-mr-sm"
                  :src="modifier.imageUrl"
                  :style="{
                    width: $q.screen.lt.sm? '20px' : '30px',
                    height: $q.screen.lt.sm? '20px' : '30px',
                    'background-color': modifier.color
                  }"
                />
                <div
                  class="col"
                >
                  <div
                    class="text-subtitle2"
                    style="line-height: 1.5;"
                  >
                    {{ modifier.name }}
                  </div>

                  <div
                    v-if="modifier.description"
                    class="text-caption"
                    style="line-height: 1;"
                  >
                    {{ modifier.description }}
                  </div>
                </div>

                <slot
                  name="price"
                  :modifier="modifier"
                >
                  <div
                    v-if="modifier.price"
                    class="col-auto"
                    style="min-width: 60px;"
                  >
                    +
                    <i18n-n
                      :value="modifier.price"
                      tag="span"
                      class="text-subtitle2"
                      format="currency"
                      scope="global"
                    />
                  </div>
                </slot>
              </q-card-section>
              <q-badge
                v-if="showAvailabilityBadge({ item, modifier })"
                class="absolute-top-right"
                :color="availabilityBadgeColor({ item, modifier })"
                :label="availableStock({ item, modifier })"
              />
            </q-card>
          </div>
        </div>
      </template>
    </q-expansion-item>
    <div
      v-else
      class="q-mb-sm relative-position"
      :class="errorMessage ? 'text-red' : undefined"
    >
      <div class="text-h5 row justify-between">
        {{ itemModifierSet.modifierSet.name }}
        <template v-if="$register">
          <q-btn
            v-if="!editMode"
            label="Edit Options"
            icon="edit"
            outline
            :loading="configuration.loading"
            @click="$emit('edit-options-button-click', () => toggleEditMode())"
          />
        </template>
      </div>
      <div>
        <template v-if="itemModifierSet.minSelectionCount">
          <b>Required</b> <span class="q-mx-xs"> &middot;</span>
        </template>
        <template v-if="errorMessage">
          <q-icon
            name="error"
            color="red"
            size="xs"
          /> {{ errorMessage }}
        </template>
        <template v-else>
          {{ itemModifierSet.choiceCountDescription }}
        </template>
      </div>
    </div>

    <div
      v-if="!isExpandable"
      :style="editMode ? 'border: 2px solid var(--q-info);' : ''"
    >
      <div
        v-if="editMode"
        class="full-width row items-center q-pa-sm q-mb-md bg-info"
        :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
      >
        <div class="col">
          <div class="row items-center">
            <q-icon
              class="col-auto q-mr-md"
              size="md"
              name="info"
            />
            <div class="col">
              <div class="text-h5">
                Editing Sellable Modifiers
              </div>
              <div>
                Toggle each modifier to enable (on) or disable (off) sales.
              </div>
            </div>
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            class="q-mr-md"
            label="Cancel"
            icon="cancel"
            outline
            color="negative"
            @click="toggleEditMode()"
          />
          <q-btn
            label="Save & Close"
            icon="save"
            color="primary"
            :loading="savingEdits || configuration.loading"
            :disable="!hasEdits"
            @click="handleSaveEdits"
          />
        </div>
      </div>
      <div
        class="row"
        :class="$q.screen.gt.xs ? 'q-col-gutter-lg' : 'q-col-gutter-sm'"
      >
        <div
          v-for="modifier in modifiersComputed"
          :key="modifier.id"
          :class="$q.screen.gt.xs ? 'col-6' : 'col-12'"
        >
          <q-card
            flat
            bordered
            class="position-relative"
            :class="{
              'bg-responsive-grey-2': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id),
              'text-red': errorMessage
            }"
            @click="handleClick(modifier)"
          >
            <q-card-section
              horizontal
              class="justify-between items-center q-pa-sm"
              :style="!editMode && reduceOpacity({ item, modifier }) ? 'opacity: 0.3;' : ''"
            >
              <q-icon
                :name="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'fas fa-square-check' : 'fal fa-square'"
                :color="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'primary' : errorMessage ? 'red' : 'responsive'"
                :class="{ 'bg-white': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) }"
                style="height: 20px;"
                class="col-auto q-mr-sm"
                size="md"
              />
              <q-img
                v-if="modifier.image"
                :ratio="1"
                class="rounded-borders q-mr-sm"
                :src="modifier.imageUrl"
                :style="{
                  width: $q.screen.lt.sm? '20px' : '30px',
                  height: $q.screen.lt.sm? '20px' : '30px',
                  'background-color': modifier.color
                }"
              />
              <div
                class="col"
              >
                <div
                  :class="$q.screen.lt.sm ? 'text-h6' : 'text-h5'"
                  style="line-height: 1.5;"
                >
                  {{ modifier.name }}
                </div>

                <div
                  v-if="modifier.description"
                  class="text-caption"
                  style="line-height: 1;"
                >
                  {{ modifier.description }}
                </div>
              </div>

              <q-toggle
                v-if="editMode"
                :model-value="areSalesEnabled(modifier)"
                :disable="modifier.loading || configuration.loading"
                @update:model-value="toggleSalesDisabled(modifier)"
              />
              <slot
                v-else
                name="price"
                :modifier="modifier"
              >
                <div
                  v-if="modifier.price"
                  class="col-auto"
                  style="min-width: 60px;"
                >
                  <i18n-n
                    :value="modifier.price"
                    tag="span"
                    class="text-subtitle1"
                    format="currency"
                    scope="global"
                  />
                </div>
              </slot>
            </q-card-section>
            <q-badge
              v-if="showAvailabilityBadge({ item, modifier })"
              class="absolute-top-right"
              :color="availabilityBadgeColor({ item, modifier })"
              :label="availableStock({ item, modifier })"
            />
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFormChild } from 'quasar'
import Model from 'src/models/model'
import SalesRestrictionsMixin from 'src/modules/mixins/SalesRestrictionsMixin'
import { useDisableSales } from 'src/modules/register/composables/disable-sales'
import { nextTick, ref, toRefs, watchEffect } from 'vue'

export default {
  name: 'ItemModifierSet',
  mixins: [SalesRestrictionsMixin],
  configuration: ['currentSite'],
  context: ['currentUser'],
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    itemOrderItem: {
      type: Object,
      required: true
    },
    itemModifierSet: {
      type: Object,
      requred: true,
      default: null
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    },
    isExpandable: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['request-authorization', 'edit-options-button-click'],
  setup (props) {
    const { itemOrderItem, itemModifierSet } = toRefs(props)

    const validating = ref(false)
    const errorMessage = ref(null)

    watchEffect(() => {
      if (validating.value) {
        errorMessage.value = itemOrderItem.value.validateForModifierSet(itemModifierSet.value)
      } else {
        errorMessage.value = null
      }
    })

    async function validate () {
      validating.value = true
      await nextTick()
      return !errorMessage.value
    }

    useFormChild({ validate })

    const {
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits
    } = useDisableSales()

    return {
      errorMessage,
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits
    }
  },
  data () {
    return {
      validating: false,
      expanded: false,
      savingEdits: false
    }
  },
  computed: {
    inventoryStrategy () {
      return this.currentSite.activeInventoryStrategy
    },
    item () {
      return this.itemOrderItem.item
    },
    isValid () {
      if (this.itemModifierSet.minSelectionCount) {
        return _.filter(this.itemOrderItem.itemModifiers.data, itemModifier => itemModifier.modifierSet.id === this.itemModifierSet.modifierSet.id).length >= this.itemModifierSet.minSelectionCount
      }

      return true
    },
    selectedModifierNames () {
      const modifierNames = _(this.itemOrderItem.itemModifiers.data)
        .filter(itemModifier => itemModifier.modifierSet.id === this.itemModifierSet.modifierSet.id)
        .map(itemModifier => itemModifier.modifier.name)
        .compact()
        .value()

      if (modifierNames.length === 0) return this.$t('common.noneSelected')

      return modifierNames.join(', ')
    },
    modifiersComputed () {
      const modifiers = this.availableOnline
        ? this.itemModifierSet?.modifierSet?.onlineModifiers || []
        : this.itemModifierSet?.modifierSet?.modifiers?.data || []

      return _.filter(modifiers, modifier => {
        if (!modifier.isActive) return false

        if (this.$register) return true

        if (this.inventoryStrategy?.excludeOutOfStockItemsForKioskAndConsumer && this.isOutOfStock({ item: this.item, modifier })) {
          return false
        }

        return !modifier.salesDisabled
      })
    }
  },
  watch: {
    isValid () {
      this.expanded = !this.isValid
    }
  },
  created () {
    _.extend(this, { configuration: Model.configuration })
  },
  methods: {
    async handleSaveEdits () {
      try {
        const selectedModifiers = _.filter(this.modifiersComputed, modifier => this.itemOrderItem.hasModifier(modifier, this.itemModifierSet.modifierSet.id))
        const modifiersToUnselect = _.filter(selectedModifiers, modifier => !this.areSalesEnabled(modifier))

        this.savingEdits = true
        await this.saveEdits()

        if (!_.isEmpty(modifiersToUnselect)) {
          _.each(modifiersToUnselect, this.toggleModifier)
        }
      } catch (e) {
        this.$rollbar.error(`Failed to save disable sales edits, exception: ${e.message}`)
      } finally {
        this.savingEdits = false
      }
    },
    toggleModifier (modifier) {
      this.itemOrderItem.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
      if (this.itemModifierSet.minSelectionCount === 1 && this.isValid) {
        this.expanded = false
      }
    },
    handleClick (modifier) {
      if (this.editMode) return

      if (this.itemOrderItem.hasModifier(modifier, this.itemModifierSet.modifierSetId)) {
        this.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
        return
      }

      this.checkForSalesRestrictions({
        item: this.item,
        modifier,
        successCallback: (adminApproved = false) => {
          this.itemOrderItem.adminApproved = adminApproved
          this.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
        }
      })
    }
  }
}

</script>
